/**
 * tickerInfo.js
 *
 * Catalogo de tickers (ETFs + acciones) que aparecen en los portafolios sugeridos.
 * Cada entrada tiene:
 *   name      Nombre completo del producto
 *   what      Una frase explicando QUE compras (lenguaje simple, sin jerga)
 *   kind      'etf' | 'stock'
 *
 * Usado en:
 *   - Investments.jsx (composicion de Mi Plan: muestra "VOO · 500 empresas USA  35%")
 *   - Otros lugares donde aparezcan tickers de portafolios
 *
 * Cubre los tickers usados en los 4 portafolios actuales:
 *   Guardian      BND, SCHD, VOO, GLD, VNQ
 *   Explorer      VOO, QQQ, BND, VWO, GLD, SCHD
 *   Visionary     QQQ, VOO, SOXX, PLTR, HIMS, DUOL, VWO, ARKK
 *   Income Bldr   SCHD, VYM, VOO, BND, O, KO
 */

export const TICKER_INFO = {
  // ETFs amplios (mercado total)
  VOO: { name: 'S&P 500', what: '500 empresas más grandes de USA', kind: 'etf' },
  QQQ: { name: 'Nasdaq 100', what: 'Top 100 tecnológicas (Apple, NVIDIA, Microsoft)', kind: 'etf' },
  VWO: { name: 'Mercados Emergentes', what: 'Acciones de China, India, Brasil, México', kind: 'etf' },

  // ETFs sectoriales
  SOXX: { name: 'Semiconductores', what: 'Empresas de chips (NVIDIA, AMD, TSMC)', kind: 'etf' },
  ARKK: { name: 'Innovación Disruptiva', what: 'Empresas como Tesla, Coinbase, Roku', kind: 'etf' },
  VNQ: { name: 'Bienes Raíces USA', what: 'REITs — bienes raíces que pagan rentas', kind: 'etf' },

  // ETFs defensivos
  BND: { name: 'Bonos Totales', what: 'Bonos del gobierno y empresas USA', kind: 'etf' },
  GLD: { name: 'Oro Físico', what: 'Lingotes de oro custodiados en bóvedas', kind: 'etf' },

  // ETFs de dividendos
  SCHD: { name: 'Dividend Aristocrat', what: 'Empresas USA con dividendos consistentes', kind: 'etf' },
  VYM: { name: 'Alto Dividendo', what: 'Empresas que reparten dividendos altos', kind: 'etf' },

  // Acciones individuales (apuestas concentradas)
  PLTR: { name: 'Palantir', what: 'Software de análisis de datos para gobiernos y empresas', kind: 'stock' },
  HIMS: { name: 'Hims & Hers Health', what: 'Telesalud directa al consumidor', kind: 'stock' },
  DUOL: { name: 'Duolingo', what: 'App de aprendizaje de idiomas con gamificación', kind: 'stock' },
  KO:   { name: 'Coca-Cola', what: 'Bebidas globales — el dividend aristocrat clásico', kind: 'stock' },
  O:    { name: 'Realty Income', what: 'Bienes raíces que pagan dividendo MENSUAL', kind: 'stock' },

  // Catalogo principal (los 15 stocks del Cerebro 2.0 que tambien tienen analisis individual)
  AAPL: { name: 'Apple', what: 'Ecosistema de hardware + software + servicios', kind: 'stock' },
  MSFT: { name: 'Microsoft', what: 'Software empresarial + nube Azure + IA', kind: 'stock' },
  NVDA: { name: 'NVIDIA', what: 'Chips de IA — el motor del boom tecnológico', kind: 'stock' },
  TSLA: { name: 'Tesla', what: 'Autos eléctricos + IA de manejo + energía solar', kind: 'stock' },
};

/**
 * Devuelve info enriquecida del ticker, o null si no esta en el catalogo.
 */
export function getTickerInfo(symbol) {
  if (!symbol) return null;
  return TICKER_INFO[symbol.toUpperCase()] || null;
}

/**
 * URL del audio narrado del ticker (~20s) en S3.
 * Schema: audio/tickers/{symbol_lower}-es.mp3
 * Generado por scripts/generate_ticker_audios.js
 */
export function getTickerAudioUrl(symbol) {
  if (!symbol) return null;
  const base = import.meta.env.VITE_AUDIO_BASE_URL || 'https://beta.flowexpay.com/audio';
  return `${base}/tickers/${symbol.toLowerCase()}-es.mp3`;
}

/**
 * Tickers que tienen analisis completo en el Cerebro 2.0 (catalogo CORE_SYMBOLS).
 * Para estos, el popup muestra el boton "Ver analisis completo →".
 * Para los demas (VNQ, VYM, O, KO, PLTR, HIMS, DUOL si no estan en Cerebro), solo audio + texto.
 */
export const CEREBRO_CATALOG = new Set([
  'AAPL', 'MSFT', 'NVDA', 'TSLA', 'PLTR', 'HIMS', 'DUOL',
  'VOO', 'QQQ', 'BND', 'GLD', 'SCHD', 'SOXX', 'ARKK', 'VWO',
]);

export function hasFullCerebroAnalysis(symbol) {
  return CEREBRO_CATALOG.has(symbol?.toUpperCase());
}
